import { PageHeader } from "@/components/page-components";
import { deslugifyText } from "@papier/shared";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@papier/ui/components/ui/breadcrumb";
import { Button } from "@papier/ui/components/ui/button";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useMatches,
  useRouter,
} from "@tanstack/react-router";
import { ArrowLeft, AltArrowRight as SlashIcon } from "@papier/ui/components/icons";
import { Fragment } from "react/jsx-runtime";

export const Route = createFileRoute("/_app/projects")({
  component: RouteComponent,
});

function RouteComponent() {
  const location = useLocation({ select: (loc) => ({ pathname: loc.pathname }) });
  const router = useRouter();
  const pathSegments = location.pathname.split("/").filter(Boolean);

  const matches = useMatches();
  const isEditRoute = matches.some(
    (match) => match.routeId === "/_app/projects/$projectSlug/templates/$templateSlug/edit",
  );
  const isProjectsRoute = matches.some((match) => match.routeId === "/_app/projects/");
  const isProjectWorkspaceRoute = matches.some(
    (match) => match.routeId === "/_app/projects/$projectSlug/_layout",
  );

  const isTemplatePreviewRoute = matches.some(
    (match) => match.routeId === "/_app/projects/$projectSlug/templates/$templateSlug/",
  );
  const isTemplateReviewRoute = matches.some(
    (match) => match.routeId === "/_app/projects/$projectSlug/templates/$templateSlug/review",
  );
  const hidePageHeader =
    isEditRoute ||
    isProjectsRoute ||
    isProjectWorkspaceRoute ||
    isTemplatePreviewRoute ||
    isTemplateReviewRoute;

  return (
    <>
      {!hidePageHeader && (
        <PageHeader>
          <div className="flex items-center justify-between gap-4 px-4 py-1.5">
            <Breadcrumb className="hidden sm:block">
              <BreadcrumbList className="sm:gap-2">
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>
                  <SlashIcon className="-rotate-10 text-muted-foreground/50" />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/projects">Projects</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {pathSegments.length > 1 &&
                  pathSegments.slice(1).map((segment, index) => {
                    const to = `/${pathSegments.slice(0, index + 2).join("/")}`;
                    const isLast = index === pathSegments.length - 2;
                    const displayName = deslugifyText(segment);

                    return (
                      <Fragment key={to}>
                        <BreadcrumbSeparator>
                          <SlashIcon className="-rotate-10 text-muted-foreground/50" />
                        </BreadcrumbSeparator>
                        <BreadcrumbItem>
                          {isLast ? (
                            <BreadcrumbPage>{displayName}</BreadcrumbPage>
                          ) : (
                            <BreadcrumbLink asChild className="max-w-24 truncate">
                              <Link to={to}>{displayName}</Link>
                            </BreadcrumbLink>
                          )}
                        </BreadcrumbItem>
                      </Fragment>
                    );
                  })}
              </BreadcrumbList>
            </Breadcrumb>
            <Button
              onClick={() => router.history.back()}
              variant={"outline"}
              size={"sm"}
              className="sm:hidden"
            >
              <ArrowLeft /> Back
            </Button>
          </div>
        </PageHeader>
      )}
      <Outlet />
    </>
  );
}
